/**
 * 4-tile KPI row for the Dashboard. Mirrors the design's `.kpi-row`:
 * Portfolio Value · Total Equity · Monthly Cash Flow · Pipeline Deals.
 */
import {
  Building2,
  DollarSign,
  GitFork,
  TrendingDown,
  TrendingUp,
  Wallet,
} from "lucide-react";

import { cn } from "@/lib/utils";

import { fmtMoney, type DashboardKpis } from "./dashboard-metrics";

interface DashboardKpiRowProps {
  kpis: DashboardKpis;
  ownedCount: number;
  pipelineCount: number;
  pipelineVolume: number;
}

const ACCENTS = {
  blue: "bg-blue-500/15 text-blue-400",
  green: "bg-success/15 text-success",
  amber: "bg-warning/15 text-warning",
  red: "bg-primary/15 text-primary",
} as const;

export function DashboardKpiRow({
  kpis,
  ownedCount,
  pipelineCount,
  pipelineVolume,
}: DashboardKpiRowProps) {
  const propWord = ownedCount === 1 ? "property" : "properties";
  const dealWord = pipelineCount === 1 ? "deal" : "deals";
  const EquityTrend = kpis.totalEquity >= 0 ? TrendingUp : TrendingDown;
  const CashTrend = kpis.totalCashFlow >= 0 ? TrendingUp : TrendingDown;

  const tiles: Array<{
    label: string;
    icon: typeof DollarSign;
    accent: keyof typeof ACCENTS;
    value: string;
    valueTone?: "pos" | "neg";
    subIcon: typeof DollarSign;
    sub: string;
  }> = [
    {
      label: "Portfolio Value",
      icon: DollarSign,
      accent: "blue",
      value: fmtMoney(kpis.totalValue),
      subIcon: Building2,
      sub: `${ownedCount} ${propWord} · sum of ARVs`,
    },
    {
      label: "Total Equity",
      icon: TrendingUp,
      accent: "green",
      value: fmtMoney(kpis.totalEquity),
      valueTone: kpis.totalEquity >= 0 ? "pos" : "neg",
      subIcon: EquityTrend,
      sub: "ARV less purchase across portfolio",
    },
    {
      label: "Monthly Cash Flow",
      icon: Wallet,
      accent: "amber",
      value: fmtMoney(kpis.totalCashFlow),
      valueTone: kpis.totalCashFlow >= 0 ? "pos" : "neg",
      subIcon: CashTrend,
      sub: `${fmtMoney(kpis.totalCashFlow * 12)}/yr · active rentals only`,
    },
    {
      label: "Pipeline Deals",
      icon: GitFork,
      accent: "red",
      value: String(pipelineCount),
      subIcon: GitFork,
      sub: `${fmtMoney(pipelineVolume)} in deal volume`,
    },
  ];

  return (
    <div className="grid grid-cols-2 gap-3.5 lg:grid-cols-4">
      {tiles.map((tile) => (
        <div
          key={tile.label}
          className="flex flex-col gap-2.5 rounded-[10px] border border-border bg-card p-[18px]"
        >
          <div className="flex items-start justify-between gap-2.5">
            <span className="text-[13px] font-semibold text-muted-foreground">
              {tile.label}
            </span>
            <span
              className={cn(
                "grid h-[34px] w-[34px] flex-shrink-0 place-items-center rounded-[9px]",
                ACCENTS[tile.accent],
              )}
            >
              <tile.icon className="h-4 w-4" aria-hidden />
            </span>
          </div>
          <div
            className={cn(
              "text-[26px] font-extrabold leading-[1.15] tabular-nums",
              tile.valueTone === "pos"
                ? "text-success"
                : tile.valueTone === "neg"
                  ? "text-destructive"
                  : "text-foreground",
            )}
          >
            {tile.value}
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <tile.subIcon className="h-[13px] w-[13px] flex-shrink-0" aria-hidden />
            {tile.sub}
          </div>
        </div>
      ))}
    </div>
  );
}
